import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@vinoga/db";
import { Search, X, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

type FeaturedTile = {
  eyebrow?: string;
  head?: string;
  cta?: string;
  ctaHref?: string;
  image?: string;
  bg?: string;
};

type CategoryRow = {
  id: string;
  parent_id: string | null;
  slug: string;
  translations: {
    lv?: { name?: string; description?: string };
    en?: { name?: string; description?: string };
    ru?: { name?: string; description?: string };
  } | null;
  position: number;
  active: boolean;
  color: string | null;
  meta: { featured?: FeaturedTile } | null;
};

// Order so each parent is directly followed by its children — keeps the
// table readable when the admin sorts everything by hierarchy.
function tree(rows: CategoryRow[]): Array<CategoryRow & { depth: number }> {
  const byParent = new Map<string | null, CategoryRow[]>();
  for (const r of rows) {
    const list = byParent.get(r.parent_id) ?? [];
    list.push(r);
    byParent.set(r.parent_id, list);
  }
  for (const list of byParent.values()) {
    list.sort(
      (a, b) =>
        a.position - b.position ||
        (a.translations?.lv?.name ?? "").localeCompare(b.translations?.lv?.name ?? "")
    );
  }
  const out: Array<CategoryRow & { depth: number }> = [];
  const walk = (parent: string | null, depth: number) => {
    for (const r of byParent.get(parent) ?? []) {
      out.push({ ...r, depth });
      walk(r.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

const indentForDepth = (d: number) => ({ paddingLeft: 8 + d * 16 });

export function Categories() {
  const t = useT();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const [g, pc] = await Promise.all([
      supabase.from("categories").select("*"),
      supabase.from("product_categories").select("category_id"),
    ]);
    if (g.error) {
      toast({
        title: t("categories.errorLoading", "Failed to load categories"),
        description: g.error.message,
        variant: "destructive",
      });
    } else {
      setRows((g.data ?? []) as CategoryRow[]);
    }
    const c: Record<string, number> = {};
    (pc.data ?? []).forEach((r: any) => {
      c[r.category_id] = (c[r.category_id] ?? 0) + 1;
    });
    setCounts(c);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ordered = useMemo(() => tree(rows), [rows]);
  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return ordered.filter((r) => {
      if (!showInactive && !r.active) return false;
      if (!s) return true;
      return (
        r.slug.toLowerCase().includes(s) ||
        (r.translations?.lv?.name ?? "").toLowerCase().includes(s)
      );
    });
  }, [ordered, search, showInactive]);

  const remove = async (id: string) => {
    const used = counts[id] ?? 0;
    if (used > 0) {
      toast({
        title: t("categories.cantDelete", "Cannot delete"),
        description: t(
          "categories.inUse",
          `This category is used by ${used} product(s). Move products first.`
        ),
        variant: "destructive",
      });
      return;
    }
    if (!confirm(t("categories.confirmDelete", "Delete this category?"))) return;
    const { error } = await supabase.from("categories").delete().eq("id", id);
    if (error) {
      toast({
        title: t("common.deleteFailed", "Delete failed"),
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-xl font-bold text-foreground">
            {t("categories.title", "Categories")}
            {rows.length > 0 && (
              <span className="ml-2 font-body text-sm font-normal text-muted-foreground">
                ({rows.length})
              </span>
            )}
          </h1>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="text"
              placeholder={t("categories.searchPlaceholder", "Search by name or slug...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-[260px] pl-8 pr-8 font-body text-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            {t("categories.showInactive", "Show inactive")}
          </label>
        </div>
        <Button size="sm" onClick={() => navigate("/categories/new")}>
          <Plus size={16} />
          {t("categories.add", "Add category")}
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        {t(
          "categories.subtitle",
          "Storefront navigation is generated from this tree — top-level rows are the nav items, their children populate the mega menu. Drag-free reorder: edit the 'Position' value."
        )}
      </p>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("categories.colNameLv", "Name (LV)")}</TableHead>
              <TableHead>{t("categories.colSlug", "Slug")}</TableHead>
              <TableHead className="text-right">
                {t("categories.colUsedIn", "Used in products")}
              </TableHead>
              <TableHead className="text-right">
                {t("categories.colPosition", "Position")}
              </TableHead>
              <TableHead>{t("categories.colStatus", "Status")}</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  {t("common.loading", "Loading…")}
                </TableCell>
              </TableRow>
            )}
            {!loading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  {t("categories.empty", "No categories.")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              visible.map((g) => (
                <TableRow
                  key={g.id}
                  className={`cursor-pointer hover:bg-muted/30 ${
                    !g.active ? "opacity-60" : ""
                  }`}
                  onClick={() => navigate(`/categories/${g.id}`)}
                >
                  <TableCell className="font-medium" style={indentForDepth(g.depth)}>
                    {g.depth > 0 && <span className="text-muted-foreground">└─ </span>}
                    {g.translations?.lv?.name ?? "—"}
                    {g.color && (
                      <span
                        className="ml-2 inline-block h-3 w-3 rounded-full align-middle"
                        style={{ background: g.color }}
                        aria-hidden
                      />
                    )}
                    {g.meta?.featured?.head && (
                      <span
                        className="ml-2 align-middle text-xs uppercase tracking-wider text-muted-foreground"
                        title="Has featured tile"
                      >
                        ★
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {g.slug || "—"}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {counts[g.id] ?? 0}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{g.position}</TableCell>
                  <TableCell>
                    <Badge variant={g.active ? "default" : "secondary"}>
                      {g.active
                        ? t("categories.statusActive", "active")
                        : t("categories.statusInactive", "inactive")}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(g.id);
                      }}
                      className="h-7 hover:text-destructive"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
